"use client";

import React from "react";
import { Compass, Droplets, Layers, Maximize2, ShieldCheck, Sparkles, Columns3, Scaling, Hammer, ArrowUpFromLine, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface PlanningFactor {
  title: string;
  description: string;
  icon: React.ElementType;
}
const structuralDecisions: PlanningFactor[] = [{
  title: "Moisture management",
  description: "Vapor barriers, subfloor moisture mitigation, and proper indoor air ventilation must be addressed before insulation and drywall installation.",
  icon: Droplets
}, {
  title: "Egress window design",
  description: "Local building codes mandate designated emergency escape wells and properly sized window openings for any finished bedroom or living suite.",
  icon: Maximize2
}, {
  title: "Ceiling height",
  description: "Soffit placement around HVAC ductwork, structural steel beams, and plumbing stacks determines allowable finished ceiling clearances.",
  icon: ArrowUpFromLine
}, {
  title: "Finish level",
  description: "Deciding between drywall ceilings versus acoustic baffles, recessed LED lighting arrays, and trim detailing sets the craftsmanship standard.",
  icon: Sparkles
}, {
  title: "Room layout",
  description: "Placement of stairwell transitions, wet bar plumbing runs, mechanical closets, bathrooms, and acoustic separation for entertainment zones.",
  icon: Columns3
}];
const costDrivers: PlanningFactor[] = [{
  title: "Waterproofing needs",
  description: "Interior French drains, sump pump installations, and perimeter sealants required to ensure a completely dry, long-term foundation envelope.",
  icon: ShieldCheck
}, {
  title: "Ceiling height & structural work",
  description: "Ductwork reconfiguration, beam re-engineering, or foundation excavation required to maximize vertical space and eliminate low clearance areas.",
  icon: Hammer
}, {
  title: "Egress window additions",
  description: "Concrete foundation cutting, window well excavation, structural lintels, and safety drainage tied directly to exterior storm lines.",
  icon: Scaling
}, {
  title: "Finish level choice",
  description: "Material selections such as custom millwork, natural stone surfaces, soundproofing insulation, luxury vinyl plank, or tile flooring.",
  icon: Layers
}, {
  title: "Square footage",
  description: "Overall footprint requiring framing, electrical subpanel expansion, dedicated HVAC zones, insulation, and wallboard finishing.",
  icon: Compass
}];
export default function BasementPlanning() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  return <section id="basementplanningfactors" data-nav="light" ref={ref} className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        {/* Section Header */}
        <div className={`max-w-3xl mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        opacity: isVisible ? 1 : 0
      }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs uppercase tracking-wider font-semibold mb-4 border border-border">
            <CheckCircle2 className="size-3.5 text-primary" />
            Engineering & Investment Considerations
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground leading-tight">
            Key decisions that shape your finished basement
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Transforming a lower level involves specific architectural, safety,
            and municipal standards. Review the essential building codes,
            layout requirements, and project scopes that determine the overall
            investment before work begins.
          </p>
        </div>

        {/* Dual Grid: Structural Decisions & Cost Drivers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {/* Column 1: Layout, Structural & Code Decisions */}
          <div className={`transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          opacity: isVisible ? 1 : 0
        }}>
            <Card className="h-full border border-border bg-card shadow-md flex flex-col justify-between">
              <div>
                <CardHeader className="pb-4 border-b border-border/60">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-primary/15 text-primary">
                      <Compass className="size-5" />
                    </div>
                    <div>
                      <CardTitle className="text-xl sm:text-2xl font-serif font-bold text-foreground">
                        Layout, Structural & Code Decisions
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                        Architectural parameters and safety requirements every
                        homeowner navigates
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-5">
                    {structuralDecisions.map((item, i) => {
                    const IconComponent = item.icon;
                    return <li key={item.title} data-index={i} className="flex items-start gap-4">
                          <div className="mt-1 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground border border-border/80">
                            <IconComponent className="size-4 text-primary" />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-foreground leading-snug">
                              {item.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </li>;
                  })}
                  </ul>
                </CardContent>
              </div>

              {/* Photo representation */}
              <div className="p-6 pt-2">
                <div className="relative overflow-hidden rounded-lg border border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodeling_framing_layout.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodeling_framing_layout.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodeling_framing_layout.png-wvc-sizes" alt="Finished basement living room layout with recessed lighting and high ceilings in Brookhaven" className="w-full h-48 sm:h-56 object-cover" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-4">
                    <span className="text-xs text-white/90 font-medium tracking-wide">
                      Proper architectural routing maintains clean ceiling lines
                      and open room flow.
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Column 2: Cost-Driving Factors */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          opacity: isVisible ? 1 : 0
        }}>
            <Card className="h-full border border-border bg-card shadow-md flex flex-col justify-between">
              <div>
                <CardHeader className="pb-4 border-b border-border/60">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                      <Layers className="size-5" />
                    </div>
                    <div>
                      <CardTitle className="text-xl sm:text-2xl font-serif font-bold text-foreground">
                        Cost-Driving Factors
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                        Technical scopes and physical elements that shape your
                        overall project scope
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-5">
                    {costDrivers.map((item, i) => {
                    const IconComponent = item.icon;
                    return <li key={item.title} data-index={i} className="flex items-start gap-4">
                          <div className="mt-1 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground border border-border/80">
                            <IconComponent className="size-4 text-primary" />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-foreground leading-snug">
                              {item.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </li>;
                  })}
                  </ul>
                </CardContent>
              </div>

              {/* Photo representation */}
              <div className="p-6 pt-2">
                <div className="relative overflow-hidden rounded-lg border border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_egress_window_installation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_egress_window_installation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_egress_window_installation.png-wvc-sizes" alt="Code-compliant egress window well installation providing natural daylight to renovated lower level" className="w-full h-48 sm:h-56 object-cover" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-4">
                    <span className="text-xs text-white/90 font-medium tracking-wide">
                      Excavating foundation walls for code egress wells ensures
                      safety and value.
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Informative Closing Callout */}
        <div className={`mt-12 p-6 sm:p-8 rounded-xl bg-muted/60 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        opacity: isVisible ? 1 : 0
      }}>
          <div className="max-w-2xl">
            <h3 className="text-lg font-serif font-bold text-foreground">
              Planning your Brookhaven lower level renovation?
            </h3>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              We conduct comprehensive on-site inspections of foundation
              integrity, moisture levels, and mechanical systems prior to
              crafting detailed architectural plans.
            </p>
          </div>
          <Button asChild size="lg" className="whitespace-nowrap shrink-0">
            <Link to="/ContactRequestAnEstimatePage">Request an on-site consultation</Link>
          </Button>
        </div>
      </div>
    </section>;
}